/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable react/no-array-index-key */
import { FC, memo, useEffect, useState } from 'react';
import Image from '@components/Image';
import GTM from '@utils/gtm';
import { useMedia, useTranslation as t } from '@utils/hooks';
import type { DatoProductImage } from '@framework/api/types';
import ImageModal from '@components/PDP/ColorSelect/ImageModal';
import ProductSlider from '@components/PDP/ProductSlider';
import * as styles from '@components/PDP/ColorSelect/styles';

type Props = {
  images: DatoProductImage[];
  productName: string;
  showThumbnails?: boolean;
  gradient?: string;
};

const ColorSelectGallery: FC<Props> = ({
  images,
  productName,
  showThumbnails = true,
  gradient = null,
}) => {
  const isSm = useMedia('sm');
  const isMd = useMedia('md');

  const [ready, setReady] = useState(false);
  const [open, setOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(null);

  const IMAGE_MODAL_LABEL = t('image_zoom_modal');

  useEffect(() => {
    const timer = setTimeout(() => setReady(true), 1);

    return () => {
      clearTimeout(timer);
    };
  }, [images]);

  const maxNumberOfImages = images.length >= 6 ? images.slice(0, 6) : images;

  const handleImageClickLg = (index: number): void => {
    setOpen(true);
    setModalIndex(index);
    GTM.dataLayer({
      dataLayer: {
        event: 'imageInteraction',
        interactionType: 'openModal',
      },
    });
  };

  return (
    <>
      <div style={{ aspectRatio: 'auto' }}>
        {ready &&
          Array.isArray(maxNumberOfImages) &&
          maxNumberOfImages.length > 0 && (
            <ProductSlider
              key={images[0].id}
              images={maxNumberOfImages}
              desktopSlider={!isSm && !isMd && showThumbnails}
              modalIndex={modalIndex}
              productName={productName}
              openModal={() => setOpen(true)}
              gradient={gradient}
              isSliderCompact={
                process.env.NEXT_PUBLIC_FEATURE_FLEX_SLIDER_COMPACT === 'true'
              }
            >
              {maxNumberOfImages.map((image, i) => (
                <div
                  key={`${image.id}-${i}`}
                  className={
                    (!isSm || !isMd) && gradient ? undefined : styles.imageItem
                  }
                  aria-hidden
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: image.responsiveImage.height / 1.33,
                    }}
                  >
                    {isSm || isMd ? (
                      <Image
                        className={isSm || isMd ? styles.img : undefined}
                        data={image.responsiveImage}
                        width={image.responsiveImage.width}
                        height={image.responsiveImage.height}
                        loading="eager"
                      />
                    ) : (
                      <a
                        href="#"
                        onClick={() => handleImageClickLg(i)}
                        tabIndex={-1}
                      >
                        <Image
                          className={isSm || isMd ? styles.img : undefined}
                          data={image.responsiveImage}
                          width={image.responsiveImage.width}
                          height={image.responsiveImage.height}
                          pictureStyle={{
                            width: image.responsiveImage.height / 2,
                            height: image.responsiveImage.height / 2,
                          }}
                          loading="eager"
                        />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </ProductSlider>
          )}
      </div>
      <ImageModal
        open={open}
        onClose={() => setOpen(false)}
        ariaLabel={() => IMAGE_MODAL_LABEL}
      >
        {ready &&
          Array.isArray(maxNumberOfImages) &&
          maxNumberOfImages.length > 0 && (
            <div className={styles.modalSliderWrapper}>
              <ProductSlider
                key={images[0].id}
                images={maxNumberOfImages}
                isModalOpen={open}
                modalIndex={modalIndex}
                productName={productName}
              >
                {maxNumberOfImages.map((image, i) => (
                  <div
                    key={`${image.id}-${i}`}
                    className={(!isSm || !isMd) && styles.imageItem}
                  >
                    <Image
                      data={image.responsiveImage}
                      width={image.responsiveImage.width}
                      height={image.responsiveImage.height}
                      loading="eager"
                    />
                  </div>
                ))}
              </ProductSlider>
            </div>
          )}
      </ImageModal>
    </>
  );
};

export default memo(ColorSelectGallery);
